import { Primitive } from '@radix-ui/react-primitive';
import { constant } from 'lodash';
import { type ComponentPropsWithoutRef, memo } from 'react';
import { type DropTargetMonitor, useDragLayer, useDrop } from 'react-dnd';

import { cn } from '@air/utils-tailwind';

import { type DragItem, type DragType, type DropData, type DropLocation } from '~/components/Shared/Drag/dragTypes';

export type DNDRearrangeAreaProps<T extends DragItem> = ComponentPropsWithoutRef<typeof Primitive.div> & {
  dropLocation: DropLocation;
  onItemRearrange: (draggedItem: T) => void;
  canDropItem?: (draggedItem: T) => boolean;
  dragTypes: DragType[];
  side: 'top' | 'bottom';
};

const collectDropData = <T extends DragItem>(canDropItem: DNDRearrangeAreaProps<T>['canDropItem'] = constant(true)) => {
  return (monitor: DropTargetMonitor<T>): { isOver: boolean } => {
    const isOver = monitor.isOver({ shallow: false });
    const item = monitor.getItem();

    return {
      isOver: isOver && item && canDropItem(item),
    };
  };
};

/**
 * This component is used to handle rearranging boards and workspaces in navigation
 * As well as rearranging versions in the asset modal
 * It displays horizontal line if dragged item is over it
 */
const _DNDRearrangeArea = <T extends DragItem>({
  className,
  canDropItem = constant(true),
  dragTypes,
  dropLocation,
  onItemRearrange,
  side,
  ...restOfProps
}: DNDRearrangeAreaProps<T>) => {
  const onDrop = (item: T): DropData => {
    const canDrop = canDropItem(item);

    if (canDrop) {
      onItemRearrange(item);

      return {
        type: 'rearrange',
        location: dropLocation,
      };
    }

    // Return rejected type to ensure drag state is properly cleared
    return {
      type: 'rejected',
      location: dropLocation,
    };
  };

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const [{ isOver }, drop] = useDrop({
    accept: dragTypes,
    collect: collectDropData(canDropItem),
    canDrop: (item: T) => canDropItem(item),
    drop: onDrop,
  });

  return (
    <Primitive.div
      className={cn(
        'absolute z-40 flex h-2 w-full items-center before:h-1 before:w-full before:rounded-24 before:content-[""] data-[dnd-dragging=true]:pointer-events-auto data-[side=top]:-top-1 data-[side=bottom]:-bottom-1 ads:data-[dnd-over=true]:before:bg-primary data-[dnd-over=true]:before:bg-blue-9',
        className,
      )}
      data-dnd-dragging={isDragging}
      data-dnd-over={isOver}
      data-side={side}
      {...restOfProps}
      ref={drop}
    />
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const DNDRearrangeArea = memo(_DNDRearrangeArea) as typeof _DNDRearrangeArea;
